import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Briefcase, Plus, X, CheckCircle2, Copy, ExternalLink, ArrowRight } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const FREIGHT_TYPES = [
  { code: "dry_van", label: "Dry Van" },
  { code: "reefer", label: "Reefer" },
  { code: "flatbed", label: "Flatbed" },
  { code: "step_deck", label: "Step Deck" },
  { code: "power_only", label: "Power Only" },
  { code: "hotshot", label: "Hotshot" },
  { code: "multi", label: "Multi (any of the above)" },
];

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

/**
 * CareersBuilderPage — public form to spin up a driver-hiring page in 5 min.
 * Route: /careers-builder
 * Posts to /api/careers/pages. Returns slug + edit_token that user must save.
 */
export default function CareersBuilderPage() {
  const [form, setForm] = useState({
    company_name: "", tagline: "", city: "", state: "",
    freight_type: "dry_van", pay_range: "", home_time: "",
    min_experience_years: 2, cdl_class: "A",
    contact_email: "", contact_phone: "",
  });
  const [benefits, setBenefits] = useState(["", "", ""]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.company_name || !form.city || !form.state || !form.pay_range || !form.home_time || !form.contact_email) {
      setError("Fill in the required fields (company name, city, state, pay, home time, email).");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        ...form,
        min_experience_years: parseInt(form.min_experience_years, 10) || 0,
        benefits: benefits.filter((b) => b && b.trim()),
      };
      const { data } = await axios.post(`${API_BASE}/api/careers/pages`, payload);
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const addBenefit = () => setBenefits([...benefits, ""]);
  const setBenefit = (i, v) => setBenefits(benefits.map((b, idx) => (idx === i ? v : b)));
  const removeBenefit = (i) => setBenefits(benefits.filter((_, idx) => idx !== i));

  if (result) {
    return <SuccessScreen result={result} />;
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <TopNav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #FF5A1F 0%, transparent 40%), radial-gradient(circle at 80% 70%, #FF5A1F 0%, transparent 40%)" }} />
        <div className="relative max-w-4xl mx-auto px-6 pt-16 pb-8 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#FF5A1F]/40 bg-[#FF5A1F]/10 px-4 py-1.5 text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-6">
            <Briefcase className="w-3.5 h-3.5" />
            Free driver-hiring page · Live in 5 minutes
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] tracking-tight mb-5">
            Post a driver job.
            <br />
            <span className="text-[#FF5A1F]">Get real applications.</span>
          </h1>
          <p className="text-base sm:text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
            Fill out this form → get a professional careers page at <code className="text-[#FF5A1F]">freightpilotai.ai/jobs/your-fleet</code> in under 5 minutes. Share it in Facebook groups, on TikTok, or on your business card. Drivers apply directly. No monthly fee, ever.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-16">
        <form onSubmit={submit} className="rounded-2xl border-2 border-white/10 bg-white/5 p-6 sm:p-8 space-y-5" data-testid="careers-builder-form">
          {/* Company info */}
          <Section title="Your company">
            <Field label="Company name" value={form.company_name} onChange={update("company_name")} placeholder="Yaga Empire Trucking" testid="careers-company-name" required />
            <Field label="One-line tagline" value={form.tagline} onChange={update("tagline")} placeholder="Small fleet. Big miles. Real people." testid="careers-tagline" />
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <FieldLabel>Based in (city)</FieldLabel>
                <Input value={form.city} onChange={update("city")} placeholder="Sioux Falls" testid="careers-city" required />
              </div>
              <div>
                <FieldLabel>State</FieldLabel>
                <Select value={form.state} onChange={update("state")} testid="careers-state">
                  <option value="">ST</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </Select>
              </div>
            </div>
          </Section>

          {/* The job */}
          <Section title="The job">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <FieldLabel>Freight type</FieldLabel>
                <Select value={form.freight_type} onChange={update("freight_type")} testid="careers-freight">
                  {FREIGHT_TYPES.map((f) => <option key={f.code} value={f.code}>{f.label}</option>)}
                </Select>
              </div>
              <div>
                <FieldLabel>CDL class required</FieldLabel>
                <Select value={form.cdl_class} onChange={update("cdl_class")} testid="careers-cdl">
                  <option value="A">Class A</option>
                  <option value="B">Class B</option>
                  <option value="A_or_B">A or B</option>
                </Select>
              </div>
            </div>
            <Field label="Pay range" value={form.pay_range} onChange={update("pay_range")} placeholder="$0.60-$0.70/mi (~$1,400-$1,700/wk)" testid="careers-pay" required />
            <Field label="Home time policy" value={form.home_time} onChange={update("home_time")} placeholder="2 weeks out, 3 days home" testid="careers-hometime" required />
            <div>
              <FieldLabel>Min. years of experience</FieldLabel>
              <Input type="number" value={form.min_experience_years} onChange={update("min_experience_years")} min="0" max="30" testid="careers-experience" />
            </div>
          </Section>

          {/* Benefits */}
          <Section title="What makes your fleet different (3-8 bullet points)">
            <div className="space-y-2">
              {benefits.map((b, i) => (
                <div key={i} className="flex gap-2">
                  <Input value={b} onChange={(e) => setBenefit(i, e.target.value)} placeholder="e.g., Brand new 2026 Freightliner Cascadia" testid={`careers-benefit-${i}`} />
                  {benefits.length > 1 && (
                    <button type="button" onClick={() => removeBenefit(i)}
                      className="shrink-0 w-11 rounded-lg border border-white/10 hover:border-red-500/40 hover:bg-red-500/10 text-white/40 hover:text-red-400 transition-colors flex items-center justify-center"
                      data-testid={`careers-benefit-remove-${i}`}>
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            {benefits.length < 15 && (
              <button type="button" onClick={addBenefit}
                className="text-sm text-[#FF5A1F] hover:text-[#FF7038] flex items-center gap-1 transition-colors"
                data-testid="careers-benefit-add">
                <Plus className="w-4 h-4" /> Add another
              </button>
            )}
          </Section>

          {/* Contact */}
          <Section title="How drivers reach you">
            <Field label="Contact email (applications forwarded here)" type="email" value={form.contact_email} onChange={update("contact_email")} placeholder="you@yourcompany.com" testid="careers-email" required />
            <Field label="Contact phone (optional, shown publicly)" value={form.contact_phone} onChange={update("contact_phone")} placeholder="(605) 555-1234" testid="careers-phone" />
          </Section>

          {error && (
            <div className="rounded-lg border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-200" data-testid="careers-error">
              {error}
            </div>
          )}

          <button type="submit" disabled={loading}
            className="w-full bg-[#FF5A1F] hover:bg-[#FF7038] disabled:bg-white/10 disabled:text-white/40 text-black font-bold rounded-lg px-6 py-3 text-base transition-colors flex items-center justify-center gap-2"
            data-testid="careers-submit">
            {loading ? (<><Loader2 className="w-5 h-5 animate-spin" /> Publishing...</>) : (<><Briefcase className="w-5 h-5" /> Publish my careers page</>)}
          </button>

          <p className="text-[11px] text-white/40 text-center leading-snug">
            By publishing, you confirm the information above is accurate and you're actively hiring. You'll get an <b className="text-white/70">edit_token</b> after publishing — save it, it's the only way to edit or take down the page later. Applications will be stored securely and forwarded to your email.
          </p>
        </form>
      </section>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-[12px] text-white/50">
          <div>© 2026 <b className="text-white">FreightPilotAI</b> · Careers Page Builder</div>
          <div className="flex items-center gap-4">
            <Link to="/tools" className="hover:text-white">All Tools</Link>
            <Link to="/contact" className="hover:text-white">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ─────────────────── Success screen ─────────────────── */
function SuccessScreen({ result }) {
  const [copied, setCopied] = useState(false);
  const publicUrl = `${window.location.origin}${result.public_url}`;

  const copy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <TopNav />
      <section className="max-w-3xl mx-auto px-6 py-16">
        <div className="rounded-2xl border-2 border-emerald-500/60 bg-emerald-500/10 p-8 sm:p-10 text-center" data-testid="careers-success">
          <CheckCircle2 className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
          <h1 className="text-3xl sm:text-4xl font-black mb-2 text-emerald-100 tracking-tight">
            You're live! 🎉
          </h1>
          <p className="text-emerald-200/80 max-w-lg mx-auto mb-8">
            Your careers page is now published and ready to share. Every application will be forwarded to your email automatically.
          </p>

          {/* Public URL card */}
          <div className="bg-neutral-950 border border-white/10 rounded-xl p-4 mb-4 text-left">
            <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-2">
              Your careers page URL — share this everywhere
            </div>
            <div className="flex gap-2">
              <input readOnly value={publicUrl}
                className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white/90 font-mono text-sm outline-none"
                data-testid="careers-success-url" />
              <button onClick={() => copy(publicUrl)}
                className="shrink-0 bg-[#FF5A1F] hover:bg-[#FF7038] text-black font-bold rounded-lg px-4 py-2 text-sm transition-colors flex items-center gap-1.5"
                data-testid="careers-success-copy">
                <Copy className="w-4 h-4" />
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
          </div>

          {/* Edit token card */}
          <div className="bg-amber-500/10 border-2 border-amber-500/40 rounded-xl p-4 mb-6 text-left">
            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold mb-2">
              ⚠️ Save this edit token — the only way to edit/remove later
            </div>
            <div className="flex gap-2">
              <input readOnly value={result.edit_token}
                className="flex-1 bg-black/40 border border-amber-500/20 rounded-lg px-3 py-2 text-amber-100 font-mono text-sm outline-none"
                data-testid="careers-success-token" />
              <button onClick={() => copy(result.edit_token)}
                className="shrink-0 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg px-4 py-2 text-sm transition-colors flex items-center gap-1.5"
                data-testid="careers-success-token-copy">
                <Copy className="w-4 h-4" />
                Copy
              </button>
            </div>
            <div className="text-[11px] text-amber-200/70 mt-2">
              We don't email this to you. Save it in a password manager or note file. If you lose it, you can email us to verify ownership, but that takes 2-3 days.
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to={result.public_url}
              className="inline-flex items-center justify-center gap-2 bg-[#FF5A1F] hover:bg-[#FF7038] text-black font-bold rounded-lg px-5 py-3 transition-colors"
              data-testid="careers-success-view">
              <ExternalLink className="w-4 h-4" />
              View my page
            </Link>
            <Link to={`/careers-edit?slug=${encodeURIComponent(result.slug)}&token=${encodeURIComponent(result.edit_token)}`}
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg px-5 py-3 transition-colors"
              data-testid="careers-success-edit">
              Edit my page
            </Link>
            <Link to="/tools"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-lg px-5 py-3 transition-colors">
              Browse other free tools
            </Link>
          </div>
        </div>

        <div className="mt-8 rounded-xl border border-white/10 bg-white/5 p-6">
          <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-3">
            🚀 Get maximum applications
          </div>
          <ul className="text-sm text-white/70 space-y-2 leading-relaxed">
            <li>· Share the URL in Midwest Trucking Jobs FB group (23K members)</li>
            <li>· Post it in your TikTok bio</li>
            <li>· Print it on flyers you drop at truck stops</li>
            <li>· Add it to your Facebook profile "About" section</li>
            <li>· Send it to friends who might refer a driver — best hires are referrals</li>
          </ul>
        </div>
      </section>
    </div>
  );
}

/* ─────────────────── Small helpers ─────────────────── */
function TopNav() {
  return (
    <div className="border-b border-white/10 bg-neutral-950/80 backdrop-blur sticky top-0 z-30">
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-baseline gap-1" data-testid="careers-home-link">
          <span className="font-black text-xl tracking-tight">Freight</span>
          <span className="font-black text-xl tracking-tight text-[#FF5A1F]">Pilot</span>
          <span className="font-black text-xl tracking-tight">AI</span>
        </Link>
        <div className="flex items-center gap-4 text-sm">
          <Link to="/tools" className="text-white/70 hover:text-white transition-colors hidden sm:inline">
            All Tools →
          </Link>
          <Link to="/" className="text-white/70 hover:text-white transition-colors">
            ← Home
          </Link>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-widest text-[#FF5A1F] font-bold mb-3 pb-2 border-b border-white/10">
        {title}
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function FieldLabel({ children }) {
  return <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold mb-1.5">{children}</div>;
}

function Field({ label, value, onChange, placeholder, type = "text", testid, required }) {
  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <Input type={type} value={value} onChange={onChange} placeholder={placeholder} testid={testid} required={required} />
    </div>
  );
}

function Input({ type = "text", value, onChange, placeholder, testid, required, min, max }) {
  return (
    <input type={type} value={value} onChange={onChange} placeholder={placeholder} required={required} min={min} max={max}
      className="w-full bg-neutral-900 border-2 border-white/20 hover:border-white/40 focus:border-[#FF5A1F] rounded-lg px-3 py-2.5 text-white placeholder-white/30 outline-none transition-colors"
      data-testid={testid}
    />
  );
}

function Select({ value, onChange, children, testid }) {
  return (
    <select value={value} onChange={onChange}
      className="w-full bg-neutral-900 border-2 border-white/20 hover:border-white/40 focus:border-[#FF5A1F] rounded-lg px-3 py-2.5 text-white outline-none transition-colors"
      data-testid={testid}>
      {children}
    </select>
  );
}
